import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  Check,
  CheckCircle2,
  FileText,
  Loader2,
  Upload,
  Users,
  X,
} from 'lucide-react';
import { useState } from 'react';
import { DOCUMENT_MAX_BYTES } from '@jt/domain';
import { api } from '../lib/api';
import { useSession } from '../lib/session';
import { useToast } from '../components/Toast';
import { ScrollX } from '../components/ScrollX';

/**
 * Personnel paperwork.
 *
 * Its own page rather than a tab on My work, because My work is the
 * technician's screen and a dispatcher cannot open it — while a dispatcher
 * hands in a KRA PIN exactly as everybody else does. A page nobody can reach is
 * a feature nobody has.
 *
 * Two sections, and only one of them is about you. The first is your own file.
 * The second exists for HR and shows whose file is incomplete, which is the
 * question HR actually asks — not "what arrived this morning".
 */
export function DocumentsPage() {
  const { can } = useSession();
  const toast = useToast();
  const queryClient = useQueryClient();
  const mayReview = can('document:review');

  const mine = useQuery({ queryKey: ['my-documents'], queryFn: api.myDocuments });

  const upload = useMutation({
    mutationFn: (input: { kind: string; data: string; filename: string }) =>
      api.uploadDocument(input),
    onSuccess: (document) => {
      toast.done(`${document.label} received. HR will look at it.`);
      void queryClient.invalidateQueries({ queryKey: ['my-documents'] });
      void queryClient.invalidateQueries({ queryKey: ['document-roster'] });
    },
    onError: (error: Error) => toast.failed(error.message),
  });

  /**
   * Read the file in the browser, as a data URL.
   *
   * Checked against the limit here as well as on the server, so somebody on a
   * slow connection is told a 12MB photograph is too big before spending four
   * minutes sending it — not after.
   */
  const pick = (kind: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    if (file.size > DOCUMENT_MAX_BYTES) {
      toast.failed(
        `That file is ${(file.size / 1024 / 1024).toFixed(1)}MB and the limit is 5MB. ` +
          'A photograph taken at a lower resolution is just as readable.',
      );
      return;
    }

    const reader = new FileReader();
    reader.onerror = () => toast.failed('That file could not be read. Try again.');
    reader.onload = () =>
      upload.mutate({ kind, data: String(reader.result), filename: file.name });
    reader.readAsDataURL(file);
  };

  if (mine.isPending) {
    return <Loader2 className="mx-auto mt-16 h-6 w-6 animate-spin text-primary-500" />;
  }

  const held = new Map((mine.data?.documents ?? []).map((one) => [one.kind, one]));
  const progress = mine.data?.progress;

  return (
    <div className="mx-auto max-w-3xl space-y-5 pb-16">
      <div>
        <h2 className="font-heading text-2xl">Your documents</h2>
        <p className="mt-0.5 text-sm text-muted-foreground">
          The paperwork we are required to hold for you. PDF or a clear photograph, up to 5MB
          each.
        </p>
      </div>

      {progress && (
        <div
          className={`card flex flex-wrap items-center gap-3 ${
            progress.complete ? '' : 'border-accent-300'
          }`}
        >
          {progress.complete ? (
            <CheckCircle2 className="h-5 w-5 shrink-0 text-secondary-700" aria-hidden="true" />
          ) : (
            <AlertTriangle className="h-5 w-5 shrink-0 text-accent-700" aria-hidden="true" />
          )}
          <p className="text-sm">
            {progress.complete ? (
              <span className="font-medium">Everything required is in and approved.</span>
            ) : (
              <>
                <span className="font-medium">
                  {progress.approved} of {progress.required} approved.
                </span>{' '}
                <span className="text-muted-foreground">
                  {progress.missing > 0 && `${progress.missing} not sent yet. `}
                  {progress.pending > 0 && `${progress.pending} waiting on HR. `}
                  {progress.rejected > 0 && `${progress.rejected} sent back to you.`}
                </span>
              </>
            )}
          </p>
        </div>
      )}

      <div className="space-y-2">
        {(mine.data?.kinds ?? []).map((kind) => {
          const document = held.get(kind.key);
          const locked = document?.status === 'approved';

          return (
            <section key={kind.key} className="card">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <h3 className="flex flex-wrap items-center gap-2 font-medium">
                    {kind.label}
                    {kind.required ? (
                      <span className="badge bg-muted text-muted-foreground">required</span>
                    ) : (
                      <span className="badge bg-muted text-muted-foreground">if it applies</span>
                    )}
                    {document && <StatusPill status={document.status} />}
                  </h3>
                  <p className="mt-0.5 text-xs text-muted-foreground">{kind.hint}</p>

                  {document && (
                    <p className="mt-1.5 text-xs text-muted-foreground">
                      <a
                        href={`/api/v1/documents/${document.id}/file`}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 text-primary-700 hover:underline"
                      >
                        <FileText className="h-3 w-3" aria-hidden="true" />
                        {document.originalName}
                      </a>
                      {' · '}
                      {new Date(document.uploadedAt).toLocaleDateString('en-GB', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                      })}
                    </p>
                  )}

                  {/*
                    The reason it came back, said where the person will act on
                    it. A rejection with the reason on another screen is a
                    second upload of the same wrong thing.
                  */}
                  {document?.status === 'rejected' && document.reviewNote && (
                    <p className="mt-1.5 rounded-lg bg-danger-50 px-2.5 py-1.5 text-xs text-danger-700">
                      Sent back{document.reviewedBy ? ` by ${document.reviewedBy}` : ''}:{' '}
                      {document.reviewNote}
                    </p>
                  )}
                </div>

                <div className="shrink-0">
                  {locked ? (
                    <p className="text-xs text-muted-foreground">
                      Approved, so it cannot be changed.
                      <br />
                      Ask HR if it needs replacing.
                    </p>
                  ) : (
                    <label className="btn-secondary cursor-pointer text-sm">
                      <Upload className="h-4 w-4" aria-hidden="true" />
                      {document ? 'Replace' : 'Upload'}
                      <input
                        type="file"
                        className="sr-only"
                        accept="application/pdf,image/jpeg,image/png"
                        disabled={upload.isPending}
                        onChange={pick(kind.key)}
                      />
                    </label>
                  )}
                </div>
              </div>
            </section>
          );
        })}
      </div>

      {mayReview && <ReviewSection />}
    </div>
  );
}

/** Whose file is incomplete — the question HR actually asks. */
function ReviewSection() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [openFor, setOpenFor] = useState<string | null>(null);
  const [note, setNote] = useState('');

  const roster = useQuery({ queryKey: ['document-roster'], queryFn: api.documentRoster });

  const review = useMutation({
    mutationFn: (input: { id: string; approve: boolean; note?: string }) =>
      api.reviewDocument(input.id, { approve: input.approve, note: input.note }),
    onSuccess: (document) => {
      toast.done(
        document.status === 'approved'
          ? `${document.label} approved.`
          : `${document.label} sent back.`,
      );
      setOpenFor(null);
      setNote('');
      void queryClient.invalidateQueries({ queryKey: ['document-roster'] });
      void queryClient.invalidateQueries({ queryKey: ['my-documents'] });
    },
    onError: (error: Error) => toast.failed(error.message),
  });

  const waiting = (roster.data?.people ?? []).flatMap((person) =>
    person.documents
      .filter((one) => one.status === 'pending')
      .map((one) => ({ person, document: one })),
  );

  return (
    <section className="space-y-4 pt-4">
      <div>
        <h2 className="flex items-center gap-1.5 font-heading text-xl">
          <Users className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
          Everyone&rsquo;s documents
        </h2>
        <p className="mt-0.5 text-sm text-muted-foreground">
          Nobody approves their own, including you.
        </p>
      </div>

      {roster.isPending && <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />}

      {waiting.length > 0 && (
        <div className="card space-y-3">
          <h3 className="font-heading text-lg">
            Waiting on you · {waiting.length}
          </h3>

          {waiting.map(({ person, document }) => (
            <div key={document.id} className="rounded-xl border border-border p-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm font-medium">{person.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {document.label} ·{' '}
                    <a
                      href={`/api/v1/documents/${document.id}/file`}
                      target="_blank"
                      rel="noreferrer"
                      className="text-primary-700 hover:underline"
                    >
                      open the file
                    </a>
                  </p>
                </div>

                <div className="flex gap-2">
                  <button
                    type="button"
                    className="btn-secondary px-2.5 py-1 text-xs"
                    disabled={review.isPending}
                    onClick={() => review.mutate({ id: document.id, approve: true })}
                  >
                    <Check className="h-3.5 w-3.5" aria-hidden="true" />
                    Approve
                  </button>
                  <button
                    type="button"
                    className="btn-secondary px-2.5 py-1 text-xs"
                    onClick={() => {
                      setOpenFor(openFor === document.id ? null : document.id);
                      setNote('');
                    }}
                  >
                    <X className="h-3.5 w-3.5" aria-hidden="true" />
                    Send back
                  </button>
                </div>
              </div>

              {openFor === document.id && (
                <div className="mt-2.5 space-y-2">
                  <label className="block text-xs">
                    Why is it being sent back?
                    <input
                      className="input mt-1 w-full text-sm"
                      value={note}
                      onChange={(event) => setNote(event.target.value)}
                      placeholder="The second page is missing"
                    />
                    <span className="mt-1 block text-xs text-muted-foreground">
                      They will see this. &ldquo;Not accepted&rdquo; with no reason gets the same
                      file again.
                    </span>
                  </label>
                  <button
                    type="button"
                    className="btn-danger px-3 py-1 text-xs"
                    disabled={note.trim().length < 3 || review.isPending}
                    onClick={() =>
                      review.mutate({ id: document.id, approve: false, note: note.trim() })
                    }
                  >
                    Send it back
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {roster.data && (
        <div className="card">
          <h3 className="mb-3 font-heading text-lg">Everybody</h3>
          <ScrollX>
            <table className="w-full text-sm">
              <thead className="border-b border-border text-left text-xs text-muted-foreground">
                <tr>
                  <th className="py-2 font-medium">Person</th>
                  <th className="py-2 font-medium">Role</th>
                  <th className="py-2 text-right font-medium">Approved</th>
                  <th className="py-2 text-right font-medium">Waiting</th>
                  <th className="py-2 text-right font-medium">Sent back</th>
                  <th className="py-2 text-right font-medium">Not sent</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {roster.data.people.map((person) => (
                  <tr key={person.id} className={person.progress.complete ? '' : ''}>
                    <td className="py-2">
                      <span className="font-medium">{person.name}</span>
                      {person.progress.complete && (
                        <CheckCircle2
                          className="ml-1.5 inline h-3.5 w-3.5 text-secondary-700"
                          aria-hidden="true"
                        />
                      )}
                    </td>
                    <td className="py-2 text-muted-foreground">{person.role}</td>
                    <td className="py-2 text-right tabular-nums">
                      {person.progress.approved}/{person.progress.required}
                    </td>
                    <td className="py-2 text-right tabular-nums">
                      {person.progress.pending || <span className="text-muted-foreground">—</span>}
                    </td>
                    <td className="py-2 text-right tabular-nums">
                      {person.progress.rejected ? (
                        <span className="font-semibold text-danger-700">
                          {person.progress.rejected}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="py-2 text-right tabular-nums">
                      {person.progress.missing ? (
                        <span className="font-semibold text-accent-700">
                          {person.progress.missing}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </ScrollX>
        </div>
      )}
    </section>
  );
}

function StatusPill({ status }: { status: 'pending' | 'approved' | 'rejected' }) {
  if (status === 'approved') {
    return <span className="badge bg-secondary-100 text-secondary-800">approved</span>;
  }
  if (status === 'rejected') {
    return <span className="badge bg-danger-50 text-danger-700">sent back</span>;
  }
  return <span className="badge bg-primary-100 text-primary-800">waiting on HR</span>;
}
